/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import {
    isAdvertisementEntry,
    type PlaylistController,
    type PlaylistEntry,
} from '@amzn/amp-playback'
import { promptSignInIfAnonymous } from '@/auth/promptSignInIfAnonymous'

/**
 * The skip predicates operate purely on `PlaylistController` (a playback-layer
 * type), so they live in the playback layer where both `transport/` (the
 * transport bar, media keys, lock-screen) and `track/` (the track overflow
 * menu's compact controls + dislike-advance) can import them without a
 * `track ↔ transport` bi-reference. The denial SIDE-EFFECT
 * (the transport tooltip + the DM upsell) is transport-domain, so it is
 * injected through `setSkipDenialEffect` — transport registers the effect at
 * boot and the guards below invoke it, keeping the copy/upsell knowledge in
 * transport while the mechanism stays low.
 */

/**
 * Whether an entry carries an explicit-language flag. Track-only — every
 * other entity kind (podcast, ad, audiobook) is never explicit-masked, so
 * gate on the typename first. Shared by the transport badge and the cloud
 * queue's explicit-filter omission so the "what counts as explicit" rule
 * lives in one place.
 */
export function isExplicitEntry(entry: PlaylistEntry | null): boolean {
    if (entry?.entity.__typename !== 'Track') return false
    return entry.entity.parentalSettings?.hasExplicitLanguage === true
}

/**
 * Whether the transport may advance past the current entry. Gates on the
 * per-entity `next` capability (free-tier skip caps, no-skip content) and
 * on ad breaks. Absent capabilities default permissive
 * (`ALLOW_ALL_ENTITY_CAPABILITIES`), so non-cloud / capless queues keep
 * skipping freely. Mirrors hornet's `allowedToGoNext`.
 */
export function skipForwardAllowed(controller: PlaylistController): boolean {
    if (!controller.currentCapabilities$.value.nextAllowed) return false
    if (isAdvertisementEntry(controller.current.value)) return false
    return true
}

/**
 * Whether the transport may step back to the previous entry. Gates on
 * the per-entity `previous` capability (the gateway's GetPreviousEntity
 * throws for accounts that can't navigate back) and on ad breaks.
 * Absent capabilities default permissive, so non-cloud / capless queues
 * keep navigating back freely. Mirrors hornet's `allowedToGoPrevious`.
 */
export function previousAllowed(controller: PlaylistController): boolean {
    if (!controller.currentCapabilities$.value.previousAllowed) return false
    return !isAdvertisementEntry(controller.current.value)
}

/** The controller-derived facts a denial handler needs to pick copy + upsell. */
export interface SkipDenial {
    readonly direction: 'forward' | 'previous'
    /** Ad breaks are a hard denial in either direction (never an upgrade nudge). */
    readonly isAd: boolean
    /**
     * A spent free-tier skip budget (`remainingSkips === 0`), forward only.
     * Distinguishes the skip-cap upsell from the generic feature-unavailable one.
     */
    readonly isSkipCap: boolean
}

type SkipDenialEffect = (denial: SkipDenial) => void

// DI seam: the transport-domain denial surface (tooltip + DM upsell). Null
// until transport registers it at boot; a guard denial with no effect still
// gates the gesture, it just paints no upsell.
let skipDenialEffect: SkipDenialEffect | null = null

/** Register the denial surface. Called once at boot from transport. */
export function setSkipDenialEffect(effect: SkipDenialEffect): void {
    skipDenialEffect = effect
}

/**
 * Guard for every skip-forward path (next button, swipe, keyboard, media
 * key, overflow-menu dislike-advance). Returns true when the skip is
 * permitted — the caller should then invoke `skipNext()`. When denied,
 * fires the registered upsell / feedback and returns false so the caller
 * no-ops rather than leaving a dead gesture.
 */
export function guardedSkipForward(controller: PlaylistController): boolean {
    if (promptSignInIfAnonymous()) return false
    if (skipForwardAllowed(controller)) return true
    // An ad is never a skip cap even if its edge carries remainingSkips.
    // UNLIMITED never reaches here — absent caps default permissive.
    const isAd = isAdvertisementEntry(controller.current.value)
    const isSkipCap = !isAd && controller.currentCapabilities$.value.remainingSkips === 0
    skipDenialEffect?.({ direction: 'forward', isAd, isSkipCap })
    return false
}

/**
 * Guard for the "go to previous entry" branch of every back path
 * (previous button, swipe, keyboard, media key, overflow-menu Previous).
 * Returns true when the back navigation is permitted. When denied, fires
 * the registered upsell / feedback and returns false so the caller
 * no-ops. The <3s-restart of the current track is always allowed and must
 * NOT route through here.
 */
export function guardedPrevious(controller: PlaylistController): boolean {
    if (promptSignInIfAnonymous()) return false
    if (previousAllowed(controller)) return true
    skipDenialEffect?.({
        direction: 'previous',
        isAd: isAdvertisementEntry(controller.current.value),
        isSkipCap: false,
    })
    return false
}
